import React, { useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { fetchPieces } from "../services/api";
import Spinner from "./Spinner";

const CompEntriesbyAge = ({ startDate, promptId }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAndTransformData = async () => {
      setIsLoading(true);
      try {
        const rawData = await fetchPieces(startDate, promptId);

        // Filter: submitted = true AND age is not null/None
        const filtered = rawData.filter(entry =>
          entry.raw_submitted_at && 
          entry.raw_submitted_at !== "NaT" &&
          entry.age !== null &&
          entry.age !== "None"
        );

        // Group by discrete ages 13..20 and an "Above 20" bucket
        // Track unique user_ids per age group (matching Looker Studio COUNT(user_id))
        const ageKeys = [13, 14, 15, 16, 17, 18, 19, 20];
        const usersByAge = Object.fromEntries([
          ...ageKeys.map(a => [String(a), new Set()]),
          ["Above 20", new Set()]
        ]);

        filtered.forEach(entry => {
          // Parse age from string to number
          const age = Math.floor(parseFloat(entry.age));
          const userId = entry.user_id;
          
          if (!userId || isNaN(age)) return;

          if (age >= 13 && age <= 20) {
            usersByAge[String(age)].add(userId);
          } else if (age > 20) {
            usersByAge["Above 20"].add(userId);
          }
          // ages below 13 are ignored by design
        });

        // Convert Sets to counts (distinct user count per age)
        const counts = Object.fromEntries(
          Object.entries(usersByAge).map(([age, userSet]) => [age, userSet.size])
        );

        // Convert to Nivo format in ascending order with "Above 20" last
        const orderedLabels = [...ageKeys.map(a => String(a)), "Above 20"];
        const formattedData = orderedLabels
          .map(label => ({ id: label, label, value: counts[label] }))
          .filter(d => d.value > 0);

        setData(formattedData);
      } catch (err) {
        console.error("Failed to load age-based pie chart", err);
      } finally {
        setIsLoading(false);
      }
    };

    // reset for immediate spinner on change
    setData([]);
    fetchAndTransformData();
  }, [startDate, promptId]);

  return (
    <div style={{ height: "400px", maxWidth: "700px" }}>
      {isLoading ? (
        <Spinner label="Loading chart…" />
      ) : data.length > 0 ? (
        <ResponsivePie
          data={data}
          margin={{ top: 40, right: 160, bottom: 40, left: 40 }}
          innerRadius={0.4}
          padAngle={1}
          cornerRadius={3}
          colors={{ scheme: "category10" }}
          borderWidth={1}
          borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#333333"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          // Show percentage labels on slices
          arcLabel={(d) => {
            const total = data.reduce((sum, item) => sum + item.value, 0);
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0.0";
            return `${pct}%`;
          }}
          // Custom tooltip: Bracket: count (percent)
          tooltip={({ datum }) => {
            const total = data.reduce((sum, item) => sum + item.value, 0);
            const pct = total > 0 ? ((datum.value / total) * 100).toFixed(1) : "0.0";
            return (
              <div style={{
                background: "white",
                padding: "6px 9px",
                border: "1px solid #ddd",
                borderRadius: 4,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                color: "#333"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 12, height: 12, background: datum.color, display: "inline-block", borderRadius: 2 }} />
                  <span style={{ fontWeight: 600 }}>{datum.id}</span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontWeight: 600 }}>{datum.value}</span>
                  <span>{` (${pct}%)`}</span>
                </div>
              </div>
            );
          }}
          legends={[
            {
              anchor: "right",
              direction: "column",
              translateX: 140,
              itemWidth: 100,
              itemHeight: 20,
              itemsSpacing: 2,
              symbolSize: 12,
              symbolShape: "circle"
            }
          ]}
        />
      ) : (
        <p>No data available.</p>
      )}
    </div>
  );
};

export default CompEntriesbyAge;
